import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FiFileText, FiEdit, FiTrash2, FiPlus, FiX, FiLogOut, FiBarChart2, FiTrendingUp, FiBriefcase, FiMail } from 'react-icons/fi';
import api from '../utils/api';
import toast from 'react-hot-toast';

const EMPTY_FORM = {
  title: '',
  content: '',
  url: '',
  category: 'General',
  status: 'draft',
  publish_at: '',
};

const EMPTY_PROJECT_FORM = {
  type: 'case-study',
  status: 'draft',
  sort_order: 0,
  slug: '',
  company: '',
  role: '',
  duration: '',
  tags: '',
  headline: '',
  summary: '',
  // Case study fields
  metrics: [{ icon: 'FiTrendingUp', label: '', value: '', delta: '' }],
  sections: [{ title: '', content: '' }],
  // Product fields
  tagline: '',
  platform: '',
  version: '',
  requirements: '',
  features: '',
  techStack: '',
  githubUrl: '',
};

const ICON_OPTIONS = [
  { value: 'FiTrendingUp', label: 'Trending Up' },
  { value: 'FiDollarSign', label: 'Dollar Sign' },
  { value: 'FiClock', label: 'Clock' },
  { value: 'FiUsers', label: 'Users' },
];

function AdminPanel() {
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [projects, setProjects] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const { tab: tabParam } = useParams();
  const validTabs = ['posts', 'projects', 'link-analytics', 'site-analytics', 'newsletter'];
  const activeTab = validTabs.includes(tabParam) ? tabParam : 'posts';
  const [subscribers, setSubscribers] = useState(null);
  const [nlDrafts, setNlDrafts] = useState([]);
  const [nlActiveDraft, setNlActiveDraft] = useState(null);
  const [nlRecipient, setNlRecipient] = useState('me');
  const [nlSelectedEmails, setNlSelectedEmails] = useState([]);
  const [nlSending, setNlSending] = useState(false);
  const [nlSaving, setNlSaving] = useState(false);
  const [nlShowHtml, setNlShowHtml] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [projectForm, setProjectForm] = useState(EMPTY_PROJECT_FORM);
  const navigate = useNavigate();

  useEffect(() => {
    checkAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (authenticated && activeTab === 'posts') {
      loadPosts();
    } else if (authenticated && activeTab === 'projects') {
      loadProjects();
    } else if (authenticated && (activeTab === 'link-analytics' || activeTab === 'site-analytics') && !analytics) {
      loadAnalytics();
    } else if (authenticated && activeTab === 'newsletter') {
      if (!subscribers) loadSubscribers();
      loadDrafts();
    }
  }, [authenticated, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const checkAuth = async () => {
    try {
      const response = await api.get('/auth/check');
      if (response.data.authenticated) {
        setAuthenticated(true);
      } else {
        navigate('/admin-login');
      }
    } catch {
      navigate('/admin-login');
    } finally {
      setLoading(false);
    }
  };

  const loadPosts = async () => {
    try {
      const response = await api.get('/posts/all');
      setPosts(response.data);
    } catch (error) {
      console.error('Error loading posts:', error);
      toast.error('Failed to load posts');
    }
  };

  const loadProjects = async () => {
    try {
      const response = await api.get('/projects/all');
      setProjects(response.data);
    } catch (error) {
      console.error('Error loading projects:', error);
      toast.error('Failed to load projects');
    }
  };

  const handleProjectSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataBlob = projectForm.type === 'case-study'
        ? {
            metrics: projectForm.metrics.filter(m => m.label && m.value),
            sections: projectForm.sections.filter(s => s.title && s.content),
          }
        : {
            tagline: projectForm.tagline,
            platform: projectForm.platform,
            version: projectForm.version,
            requirements: projectForm.requirements,
            features: projectForm.features.split('\n').map(f => f.trim()).filter(Boolean),
            techStack: projectForm.techStack.split(',').map(t => t.trim()).filter(Boolean),
            links: projectForm.githubUrl ? { github: projectForm.githubUrl } : {},
          };

      const payload = {
        type: projectForm.type,
        status: projectForm.status,
        sort_order: parseInt(projectForm.sort_order) || 0,
        slug: projectForm.slug || null,
        company: projectForm.company,
        role: projectForm.role,
        duration: projectForm.duration || null,
        tags: JSON.stringify(projectForm.tags.split(',').map(t => t.trim()).filter(Boolean)),
        headline: projectForm.headline,
        summary: projectForm.summary,
        data: JSON.stringify(dataBlob),
      };

      if (editingProject) {
        await api.put(`/projects/${editingProject.id}`, payload);
        toast.success('Project updated!');
      } else {
        await api.post('/projects', payload);
        toast.success('Project created!');
      }
      closeProjectModal();
      loadProjects();
    } catch (error) {
      toast.error('Error saving project: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleEditProject = (project) => {
    const data = JSON.parse(project.data || '{}');
    const tags = JSON.parse(project.tags || '[]');
    setEditingProject(project);
    setProjectForm({
      type: project.type,
      status: project.status,
      sort_order: project.sort_order,
      slug: project.slug || '',
      company: project.company,
      role: project.role,
      duration: project.duration || '',
      tags: tags.join(', '),
      headline: project.headline,
      summary: project.summary,
      metrics: data.metrics && data.metrics.length > 0 ? data.metrics : [{ icon: 'FiTrendingUp', label: '', value: '', delta: '' }],
      sections: data.sections && data.sections.length > 0 ? data.sections : [{ title: '', content: '' }],
      tagline: data.tagline || '',
      platform: data.platform || '',
      version: data.version || '',
      requirements: data.requirements || '',
      features: (data.features || []).join('\n'),
      techStack: (data.techStack || []).join(', '),
      githubUrl: data.links?.github || '',
    });
    setShowProjectModal(true);
  };

  const handleNewProject = () => {
    setEditingProject(null);
    setProjectForm(EMPTY_PROJECT_FORM);
    setShowProjectModal(true);
  };

  const closeProjectModal = () => {
    setShowProjectModal(false);
    setEditingProject(null);
    setProjectForm(EMPTY_PROJECT_FORM);
  };

  const handleDeleteProject = async (id) => {
    if (window.confirm('Delete this project? This cannot be undone.')) {
      try {
        await api.delete(`/projects/${id}`);
        toast.success('Project deleted');
        loadProjects();
      } catch (error) {
        toast.error('Error deleting project: ' + (error.response?.data?.error || error.message));
      }
    }
  };

  const addMetric = () => {
    setProjectForm({ ...projectForm, metrics: [...projectForm.metrics, { icon: 'FiTrendingUp', label: '', value: '', delta: '' }] });
  };

  const removeMetric = (index) => {
    setProjectForm({ ...projectForm, metrics: projectForm.metrics.filter((_, i) => i !== index) });
  };

  const updateMetric = (index, field, value) => {
    const updated = [...projectForm.metrics];
    updated[index] = { ...updated[index], [field]: value };
    setProjectForm({ ...projectForm, metrics: updated });
  };

  const addSection = () => {
    setProjectForm({ ...projectForm, sections: [...projectForm.sections, { title: '', content: '' }] });
  };

  const removeSection = (index) => {
    setProjectForm({ ...projectForm, sections: projectForm.sections.filter((_, i) => i !== index) });
  };

  const updateSection = (index, field, value) => {
    const updated = [...projectForm.sections];
    updated[index] = { ...updated[index], [field]: value };
    setProjectForm({ ...projectForm, sections: updated });
  };

  const loadAnalytics = async () => {
    try {
      const response = await api.get('/analytics');
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Failed to load analytics');
    }
  };

  const loadSubscribers = async () => {
    try {
      const response = await api.get('/subscribers');
      setSubscribers(response.data);
    } catch (error) {
      console.error('Error loading subscribers:', error);
      toast.error('Failed to load subscribers');
    }
  };

  const unsubscribeUser = async (id, email) => {
    if (!window.confirm(`Unsubscribe ${email}?`)) return;
    try {
      await api.delete(`/subscribers/${id}`);
      toast.success(`${email} unsubscribed`);
      loadSubscribers();
    } catch (error) {
      toast.error('Failed to unsubscribe');
    }
  };

  const deleteSubscriber = async (id, email) => {
    if (!window.confirm(`Permanently delete ${email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/subscribers/${id}?hard=true`);
      toast.success(`${email} deleted`);
      loadSubscribers();
    } catch (error) {
      toast.error('Failed to delete subscriber');
    }
  };


  const formatDraftDate = (str) => {
    if (!str) return '';
    const d = new Date(str);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const loadDrafts = async () => {
    try {
      const res = await api.get('/newsletter/drafts');
      setNlDrafts(res.data);
    } catch {}
  };

  const createDraft = async () => {
    try {
      const res = await api.post('/newsletter/drafts', {
        name: `Week ${nlDrafts.length + 1}`,
        subject: '',
        body_html: '',
      });
      setNlDrafts(prev => [res.data, ...prev]);
      setNlActiveDraft(res.data);
    } catch { toast.error('Failed to create draft'); }
  };

  const selectDraft = async (id) => {
    try {
      const res = await api.get(`/newsletter/drafts/${id}`);
      setNlActiveDraft(res.data);
      setNlShowHtml(false);
    } catch {}
  };

  const saveDraft = async () => {
    if (!nlActiveDraft) return;
    setNlSaving(true);
    try {
      const res = await api.put(`/newsletter/drafts/${nlActiveDraft.id}`, {
        name: nlActiveDraft.name,
        subject: nlActiveDraft.subject,
        body_html: nlActiveDraft.body_html,
      });
      setNlActiveDraft(res.data);
      setNlDrafts(prev => prev.map(d => d.id === res.data.id
        ? { ...d, name: res.data.name, updated_at: res.data.updated_at }
        : d
      ));
      toast.success('Draft saved');
    } catch { toast.error('Failed to save draft'); }
    finally { setNlSaving(false); }
  };

  const deleteDraft = async (id) => {
    if (!window.confirm('Delete this draft?')) return;
    try {
      await api.delete(`/newsletter/drafts/${id}`);
      setNlDrafts(prev => prev.filter(d => d.id !== id));
      if (nlActiveDraft?.id === id) setNlActiveDraft(null);
    } catch { toast.error('Failed to delete draft'); }
  };

  const sendNewsletter = async () => {
    if (!nlActiveDraft?.subject || !nlActiveDraft?.body_html) {
      toast.error('Subject and body are required');
      return;
    }
    if (nlRecipient === 'choose' && nlSelectedEmails.length === 0) {
      toast.error('Select at least one subscriber');
      return;
    }
    if (nlRecipient === 'all' && !window.confirm(`Send to all ${subscribers?.active} active subscribers?`)) return;
    setNlSending(true);
    try {
      const payload = { subject: nlActiveDraft.subject, body_html: nlActiveDraft.body_html };
      if (nlRecipient === 'me') payload.recipients = ['danielwait6492@gmail.com'];
      if (nlRecipient === 'choose') payload.recipients = nlSelectedEmails;
      const response = await api.post('/newsletter/send', payload);
      toast.success(`Sent to ${response.data.sent} of ${response.data.total}`);
      if (response.data.failed?.length > 0) {
        toast.error(`Failed: ${response.data.failed.map(f => f.email).join(', ')}`);
      }
    } catch (error) {
      toast.error('Send failed: ' + (error.response?.data?.error || error.message));
    } finally {
      setNlSending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        url: formData.url.trim() || null,
        publish_at: formData.publish_at
          ? new Date(formData.publish_at).toISOString().replace('T', ' ').split('.')[0]
          : null,
      };
      if (editingPost) {
        await api.put(`/posts/${editingPost.id}`, payload);
        toast.success('Post updated!');
      } else {
        await api.post('/posts', payload);
        toast.success('Post created!');
      }
      closeModal();
      loadPosts();
    } catch (error) {
      toast.error('Error saving post: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      content: post.content,
      url: post.url || '',
      category: post.category,
      status: post.status,
      publish_at: post.publish_at
        ? new Date(post.publish_at + 'Z').toLocaleString('sv').slice(0, 16)
        : '',
    });
    setShowModal(true);
  };

  const handleNew = () => {
    setEditingPost(null);
    setFormData(EMPTY_FORM);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPost(null);
    setFormData(EMPTY_FORM);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this post? This cannot be undone.')) {
      try {
        await api.delete(`/posts/${id}`);
        toast.success('Post deleted');
        loadPosts();
      } catch (error) {
        toast.error('Error deleting post: ' + (error.response?.data?.error || error.message));
      }
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
      navigate('/admin-login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr + 'Z').toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  };

  const isScheduled = (post) =>
    post.status === 'published' && new Date(post.publish_at) > new Date();

  const weekAgo = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);
  const publishedThisWeek = posts.filter(
    (p) => p.status === 'published' && new Date(p.created_at) > weekAgo
  ).length;
  const drafts = posts.filter((p) => p.status === 'draft').length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-16 h-16 border-4 border-black dark:border-white border-t-transparent rounded-full"
        />
      </div>
    );
  }

  if (!authenticated) return null;

  const tabs = [
    { id: 'posts', label: 'Posts', icon: FiFileText },
    { id: 'projects', label: 'Projects', icon: FiBriefcase },
    { id: 'link-analytics', label: 'Post Analytics', icon: FiBarChart2 },
    { id: 'site-analytics', label: 'Site Analytics', icon: FiTrendingUp },
    { id: 'newsletter', label: 'Newsletter', icon: FiMail },
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
            <div>
              <h1 className="text-4xl font-bold mb-2 gradient-text">Admin Panel</h1>
              <p className="text-gray-600 dark:text-gray-400">Manage Wait List posts and analytics</p>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleLogout}
              className="mt-4 md:mt-0 px-6 py-3 border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-black hover:text-black dark:hover:border-white dark:hover:text-white transition-all flex items-center space-x-2"
            >
              <FiLogOut />
              <span>Logout</span>
            </motion.button>
          </div>

          <div className="flex space-x-2 border-b border-gray-200 dark:border-gray-700">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => navigate(`/admin/${tab.id}`)}
                  className={`px-6 py-3 font-semibold flex items-center space-x-2 border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-black text-black dark:border-white dark:text-white'
                      : 'border-transparent text-gray-500 dark:text-gray-500 hover:text-black dark:hover:text-white'
                  }`}
                >
                  <Icon />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'posts' && (
            <motion.div
              key="posts"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {/* Stats */}
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="card-modern p-6 text-center">
                  <div className="text-4xl font-bold gradient-text mb-2">{posts.length}</div>
                  <div className="text-gray-600 dark:text-gray-400">Total Posts</div>
                </div>
                <div className="card-modern p-6 text-center">
                  <div className="text-4xl font-bold gradient-text mb-2">{publishedThisWeek}</div>
                  <div className="text-gray-600 dark:text-gray-400">Published This Week</div>
                </div>
                <div className="card-modern p-6 text-center">
                  <div className="text-4xl font-bold gradient-text mb-2">{drafts}</div>
                  <div className="text-gray-600 dark:text-gray-400">Drafts</div>
                </div>
              </div>

              {/* New Post Button */}
              <div className="mb-6">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleNew}
                  className="btn-modern flex items-center space-x-2"
                >
                  <FiPlus />
                  <span>New Post</span>
                </motion.button>
              </div>

              {/* Posts Table */}
              <div className="card-modern overflow-hidden">
                {posts.length === 0 ? (
                  <div className="text-center py-12">
                    <FiFileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h4 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">No posts yet</h4>
                    <p className="text-gray-600 dark:text-gray-400">Create your first post!</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Title</th>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Category</th>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Status</th>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Publish Date</th>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {posts.map((post) => (
                          <tr key={post.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                            <td className="px-6 py-4">
                              <span className="font-medium text-gray-900 dark:text-white">
                                {post.title}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-gray-600 dark:text-gray-400 text-sm">
                              {post.category}
                            </td>
                            <td className="px-6 py-4">
                              <span className={`text-xs font-semibold uppercase tracking-widest px-2 py-0.5 border ${
                                post.status === 'draft'
                                  ? 'border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400'
                                  : isScheduled(post)
                                  ? 'border-gray-400 dark:border-gray-500 text-gray-700 dark:text-gray-300'
                                  : 'border-black dark:border-white text-black dark:text-white'
                              }`}>
                                {post.status === 'draft' ? 'Draft' : isScheduled(post) ? 'Scheduled' : 'Live'}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-gray-600 dark:text-gray-400 text-sm">
                              {formatDate(post.publish_at)}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex space-x-2">
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => handleEdit(post)}
                                  className="p-2 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-black dark:hover:border-white hover:text-black dark:hover:text-white transition-colors"
                                >
                                  <FiEdit />
                                </motion.button>
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => handleDelete(post.id)}
                                  className="p-2 border border-gray-300 dark:border-gray-700 text-gray-500 dark:text-gray-500 hover:border-black hover:text-black dark:hover:border-white dark:hover:text-white transition-colors"
                                >
                                  <FiTrash2 />
                                </motion.button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === 'projects' && (
            <motion.div
              key="projects"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {/* Stats */}
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="card-modern p-6 text-center">
                  <div className="text-4xl font-bold gradient-text mb-2">{projects.length}</div>
                  <div className="text-gray-600 dark:text-gray-400">Total Projects</div>
                </div>
                <div className="card-modern p-6 text-center">
                  <div className="text-4xl font-bold gradient-text mb-2">{projects.filter(p => p.type === 'case-study').length}</div>
                  <div className="text-gray-600 dark:text-gray-400">Case Studies</div>
                </div>
                <div className="card-modern p-6 text-center">
                  <div className="text-4xl font-bold gradient-text mb-2">{projects.filter(p => p.type === 'product').length}</div>
                  <div className="text-gray-600 dark:text-gray-400">Products</div>
                </div>
              </div>

              {/* New Project Button */}
              <div className="mb-6">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleNewProject}
                  className="btn-modern flex items-center space-x-2"
                >
                  <FiPlus />
                  <span>New Project</span>
                </motion.button>
              </div>

              {/* Projects Table */}
              <div className="card-modern overflow-hidden">
                {projects.length === 0 ? (
                  <div className="text-center py-12">
                    <FiBriefcase className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                    <h4 className="text-xl font-bold mb-2 text-gray-900 dark:text-white">No projects yet</h4>
                    <p className="text-gray-600 dark:text-gray-400">Create your first project!</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 dark:bg-gray-800">
                        <tr>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Title</th>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Type</th>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Status</th>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Order</th>
                          <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {projects.map((project) => (
                          <tr key={project.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                            <td className="px-6 py-4">
                              <div>
                                <span className="font-medium text-gray-900 dark:text-white">{project.headline}</span>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{project.company}</p>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-xs font-semibold uppercase tracking-widest px-2 py-0.5 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400">
                                {project.type === 'case-study' ? 'Case Study' : 'Product'}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <span className={`text-xs font-semibold uppercase tracking-widest px-2 py-0.5 border ${
                                project.status === 'draft'
                                  ? 'border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400'
                                  : 'border-black dark:border-white text-black dark:text-white'
                              }`}>
                                {project.status === 'draft' ? 'Draft' : 'Live'}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-gray-600 dark:text-gray-400 text-sm">
                              {project.sort_order}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex space-x-2">
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => handleEditProject(project)}
                                  className="p-2 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-black dark:hover:border-white hover:text-black dark:hover:text-white transition-colors"
                                >
                                  <FiEdit />
                                </motion.button>
                                <motion.button
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  onClick={() => handleDeleteProject(project.id)}
                                  className="p-2 border border-gray-300 dark:border-gray-700 text-gray-500 dark:text-gray-500 hover:border-black hover:text-black dark:hover:border-white dark:hover:text-white transition-colors"
                                >
                                  <FiTrash2 />
                                </motion.button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === 'link-analytics' && (
            <motion.div
              key="link-analytics"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {analytics && (
                <>
                  <div className="grid md:grid-cols-3 gap-6 mb-8">
                    <div className="card-modern p-6 text-center">
                      <div className="text-4xl font-bold gradient-text mb-2">{analytics.totalLinkClicks || 0}</div>
                      <div className="text-gray-600 dark:text-gray-400">Total Clicks</div>
                    </div>
                    <div className="card-modern p-6 text-center">
                      <div className="text-4xl font-bold gradient-text mb-2">{analytics.totalWebClicks || 0}</div>
                      <div className="text-gray-600 dark:text-gray-400">From Website</div>
                    </div>
                    <div className="card-modern p-6 text-center">
                      <div className="text-4xl font-bold gradient-text mb-2">{analytics.totalEmailClicks || 0}</div>
                      <div className="text-gray-600 dark:text-gray-400">From Email</div>
                    </div>
                  </div>

                  <div className="card-modern overflow-hidden">
                    <div className="p-6">
                      <h3 className="text-2xl font-bold mb-6 flex items-center space-x-2">
                        <FiBarChart2 />
                        <span>Post Click Analytics</span>
                      </h3>
                      {analytics.linkAnalytics && analytics.linkAnalytics.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50 dark:bg-gray-800">
                              <tr>
                                <th className="px-6 py-4 text-left text-sm font-semibold">Post Title</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold">Total</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold">Web</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold">Email</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold">First Click</th>
                                <th className="px-6 py-4 text-left text-sm font-semibold">Last Click</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                              {analytics.linkAnalytics.map((item) => (
                                <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                                  <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">
                                    {item.title}
                                  </td>
                                  <td className="px-6 py-4">
                                    <span className="text-2xl font-bold text-gray-900 dark:text-white">{item.clicks}</span>
                                  </td>
                                  <td className="px-6 py-4 text-gray-600 dark:text-gray-400">{item.web_clicks || 0}</td>
                                  <td className="px-6 py-4 text-gray-600 dark:text-gray-400">{item.email_clicks || 0}</td>
                                  <td className="px-6 py-4 text-gray-600 dark:text-gray-400">
                                    {item.first_click ? new Date(item.first_click).toLocaleDateString() : 'Never'}
                                  </td>
                                  <td className="px-6 py-4 text-gray-600 dark:text-gray-400">
                                    {item.last_click ? new Date(item.last_click).toLocaleDateString() : 'Never'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-12">
                          <FiBarChart2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                          <h4 className="text-xl font-bold mb-2">No click data yet</h4>
                          <p className="text-gray-600 dark:text-gray-400">Analytics appear once people click post links.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </motion.div>
          )}

          {activeTab === 'site-analytics' && (
            <motion.div
              key="site-analytics"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {analytics && analytics.siteStats && (
                <>
                  <div className="grid md:grid-cols-4 gap-6 mb-8">
                    <div className="card-modern p-6 text-center">
                      <div className="text-4xl font-bold gradient-text mb-2">{analytics.siteStats.totalPageViews || 0}</div>
                      <div className="text-gray-600 dark:text-gray-400">Total Page Views</div>
                    </div>
                    <div className="card-modern p-6 text-center">
                      <div className="text-4xl font-bold gradient-text mb-2">{analytics.siteStats.pageViewsToday || 0}</div>
                      <div className="text-gray-600 dark:text-gray-400">Views Today</div>
                    </div>
                    <div className="card-modern p-6 text-center">
                      <div className="text-4xl font-bold gradient-text mb-2">{analytics.siteStats.totalSessions || 0}</div>
                      <div className="text-gray-600 dark:text-gray-400">Total Sessions</div>
                    </div>
                    <div className="card-modern p-6 text-center">
                      <div className="text-4xl font-bold gradient-text mb-2">{analytics.siteStats.avgPagesPerSession || 0}</div>
                      <div className="text-gray-600 dark:text-gray-400">Avg Pages/Session</div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="card-modern p-6">
                      <h4 className="text-xl font-bold mb-4 flex items-center space-x-2">
                        <FiTrendingUp />
                        <span>Popular Pages</span>
                      </h4>
                      {analytics.siteStats.popularPages && analytics.siteStats.popularPages.length > 0 ? (
                        <ul className="space-y-3">
                          {analytics.siteStats.popularPages.slice(0, 5).map((page, idx) => (
                            <li key={idx} className="flex justify-between items-center p-3 rounded-lg bg-gray-50 dark:bg-gray-800">
                              <span className="text-gray-700 dark:text-gray-300">{page.page}</span>
                              <strong className="text-gray-900 dark:text-white">{page.views}</strong>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-600 dark:text-gray-400">No page data yet</p>
                      )}
                    </div>
                    <div className="card-modern p-6">
                      <h4 className="text-xl font-bold mb-4 flex items-center space-x-2">
                        <FiTrendingUp />
                        <span>Top Countries</span>
                      </h4>
                      {analytics.siteStats.countryStats && analytics.siteStats.countryStats.length > 0 ? (
                        <ul className="space-y-3">
                          {analytics.siteStats.countryStats.slice(0, 5).map((country, idx) => (
                            <li key={idx} className="flex justify-between items-center p-3 rounded-lg bg-gray-50 dark:bg-gray-800">
                              <span className="text-gray-700 dark:text-gray-300">{country.country}</span>
                              <strong className="text-gray-900 dark:text-white">{country.count}</strong>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-gray-600 dark:text-gray-400">No country data yet</p>
                      )}
                    </div>
                  </div>
                </>
              )}
            </motion.div>
          )}
          {activeTab === 'newsletter' && (
            <motion.div
              key="newsletter"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="card-modern p-6 text-center">
                  <div className="text-4xl font-bold gradient-text mb-2">{subscribers?.total ?? '—'}</div>
                  <div className="text-gray-600 dark:text-gray-400">Total Subscribers</div>
                </div>
                <div className="card-modern p-6 text-center">
                  <div className="text-4xl font-bold gradient-text mb-2">{subscribers?.active ?? '—'}</div>
                  <div className="text-gray-600 dark:text-gray-400">Active</div>
                </div>
                <div className="card-modern p-6 text-center">
                  <div className="text-4xl font-bold gradient-text mb-2">{subscribers?.unsubscribed ?? '—'}</div>
                  <div className="text-gray-600 dark:text-gray-400">Unsubscribed</div>
                </div>
              </div>

              <div className="card-modern overflow-hidden mb-8">
                <div className="flex" style={{ minHeight: '640px' }}>
                  {/* Draft sidebar */}
                  <div className="w-52 shrink-0 border-r border-gray-200 dark:border-gray-700 flex flex-col">
                    <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                      <span className="text-xs font-bold tracking-widest text-gray-500 dark:text-gray-400 uppercase">Drafts</span>
                      <button onClick={createDraft} className="text-black dark:text-white hover:opacity-60 transition-opacity" title="New draft">
                        <FiPlus size={15} />
                      </button>
                    </div>
                    <div className="flex-1 overflow-y-auto">
                      {nlDrafts.length === 0 && (
                        <div className="p-4 text-xs text-gray-400 text-center">No drafts yet</div>
                      )}
                      {nlDrafts.map(draft => (
                        <button
                          key={draft.id}
                          onClick={() => selectDraft(draft.id)}
                          className={`w-full text-left px-4 py-3 border-b border-gray-100 dark:border-gray-800 group flex items-start justify-between hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors ${nlActiveDraft?.id === draft.id ? 'bg-gray-100 dark:bg-gray-800' : ''}`}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{draft.name}</div>
                            <div className="text-xs text-gray-400 mt-0.5">{formatDraftDate(draft.updated_at)}</div>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); deleteDraft(draft.id); }}
                            className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all ml-2 mt-0.5 shrink-0"
                          >
                            <FiTrash2 size={12} />
                          </button>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Main panel */}
                  <div className="flex-1 flex flex-col min-w-0">
                    {nlActiveDraft ? (
                      <>
                        {/* Controls bar */}
                        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 space-y-3">
                          <div className="flex items-center gap-3">
                            <input
                              type="text"
                              value={nlActiveDraft.name}
                              onChange={(e) => setNlActiveDraft(d => ({ ...d, name: e.target.value }))}
                              className="flex-1 text-base font-bold bg-transparent border-0 outline-none text-gray-900 dark:text-white placeholder-gray-400"
                              placeholder="Draft name"
                            />
                            <button
                              onClick={saveDraft}
                              disabled={nlSaving}
                              className="btn-modern-outline px-3 py-1 text-xs disabled:opacity-50"
                            >
                              {nlSaving ? 'Saving…' : 'Save'}
                            </button>
                          </div>
                          <input
                            type="text"
                            value={nlActiveDraft.subject}
                            onChange={(e) => setNlActiveDraft(d => ({ ...d, subject: e.target.value }))}
                            placeholder="Subject line"
                            className="w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-black dark:focus:border-white"
                          />
                          <div className="flex items-center gap-3 flex-wrap">
                            <select
                              value={nlRecipient}
                              onChange={(e) => { setNlRecipient(e.target.value); setNlSelectedEmails([]); }}
                              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white focus:outline-none focus:border-black dark:focus:border-white"
                            >
                              <option value="me">Just me (test)</option>
                              <option value="all">All subscribers ({subscribers?.active ?? 0} active)</option>
                              <option value="choose">Choose subscribers…</option>
                            </select>
                            <motion.button
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                              onClick={sendNewsletter}
                              disabled={nlSending || !nlActiveDraft.subject || !nlActiveDraft.body_html}
                              className="btn-modern px-5 py-1.5 text-sm disabled:opacity-40"
                            >
                              {nlSending ? 'Sending…' : nlRecipient === 'me' ? 'Send to me' : nlRecipient === 'choose' ? `Send to ${nlSelectedEmails.length} selected` : 'Send to all'}
                            </motion.button>
                            <button
                              onClick={() => setNlShowHtml(v => !v)}
                              className="ml-auto text-xs text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                            >
                              {nlShowHtml ? 'Show preview' : 'Edit HTML'}
                            </button>
                          </div>
                          {nlRecipient === 'choose' && (
                            <div className="border border-gray-200 dark:border-gray-700 max-h-36 overflow-y-auto">
                              {subscribers?.subscribers?.filter(s => s.status === 'active').map(s => (
                                <label key={s.id} className="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800/50 cursor-pointer border-b border-gray-100 dark:border-gray-800 last:border-0">
                                  <input
                                    type="checkbox"
                                    checked={nlSelectedEmails.includes(s.email)}
                                    onChange={(e) => setNlSelectedEmails(prev =>
                                      e.target.checked ? [...prev, s.email] : prev.filter(em => em !== s.email)
                                    )}
                                    className="accent-black dark:accent-white"
                                  />
                                  <span className="text-sm text-gray-900 dark:text-white">{s.name || s.email}</span>
                                  <span className="text-xs text-gray-400 ml-auto">{s.email}</span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Preview or HTML editor */}
                        {nlShowHtml ? (
                          <textarea
                            value={nlActiveDraft.body_html}
                            onChange={(e) => setNlActiveDraft(d => ({ ...d, body_html: e.target.value }))}
                            className="flex-1 p-4 font-mono text-xs bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white resize-none focus:outline-none"
                            spellCheck={false}
                            placeholder="Paste HTML here..."
                          />
                        ) : (
                          <div className="flex-1 overflow-auto bg-gray-100 dark:bg-gray-950 p-6">
                            {nlActiveDraft.body_html ? (
                              <iframe
                                srcDoc={nlActiveDraft.body_html}
                                title="Newsletter Preview"
                                className="w-full bg-white"
                                style={{ minHeight: '480px', border: 'none', display: 'block' }}
                                sandbox="allow-same-origin"
                              />
                            ) : (
                              <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                                No content yet — ask Claude to compose your newsletter, then save it here.
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="flex-1 flex items-center justify-center text-gray-400">
                        <div className="text-center">
                          <FiMail size={36} className="mx-auto mb-3 opacity-20" />
                          <p className="text-sm font-medium">Select a draft or create a new one</p>
                          <button onClick={createDraft} className="mt-3 text-xs text-black dark:text-white font-semibold underline underline-offset-2">
                            + New draft
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="card-modern p-6 mt-8">
                <h4 className="text-xl font-bold mb-4 flex items-center space-x-2">
                  <FiMail />
                  <span>Subscribers</span>
                </h4>
                {subscribers?.subscribers?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700">
                          <th className="text-left py-2 pr-4 font-semibold text-gray-700 dark:text-gray-300">Email</th>
                          <th className="text-left py-2 pr-4 font-semibold text-gray-700 dark:text-gray-300">Name</th>
                          <th className="text-left py-2 pr-4 font-semibold text-gray-700 dark:text-gray-300">Status</th>
                          <th className="text-left py-2 font-semibold text-gray-700 dark:text-gray-300">Subscribed</th>
                          <th className="py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {subscribers.subscribers.map((s) => (
                          <tr key={s.id} className="border-b border-gray-100 dark:border-gray-800">
                            <td className="py-2 pr-4 text-gray-900 dark:text-white">{s.email}</td>
                            <td className="py-2 pr-4 text-gray-600 dark:text-gray-400">{s.name || '—'}</td>
                            <td className="py-2 pr-4">
                              <span className={`text-xs font-semibold px-2 py-0.5 ${s.status === 'active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}`}>
                                {s.status}
                              </span>
                            </td>
                            <td className="py-2 text-gray-500 dark:text-gray-400 text-xs">{s.subscribed_at}</td>
                            <td className="py-2 text-right space-x-3">
                              {s.status === 'active' && (
                                <button
                                  onClick={() => unsubscribeUser(s.id, s.email)}
                                  className="text-xs text-red-500 hover:text-red-700 font-semibold"
                                >
                                  Unsubscribe
                                </button>
                              )}
                              <button
                                onClick={() => deleteSubscriber(s.id, s.email)}
                                className="text-xs text-gray-400 hover:text-red-700 font-semibold"
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-600 dark:text-gray-400">No subscribers yet.</p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Post Create/Edit Modal */}
        <AnimatePresence>
          {showModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto"
              onClick={closeModal}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="card-modern p-8 w-full max-w-2xl my-8"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold flex items-center space-x-2">
                    <FiFileText />
                    <span>{editingPost ? 'Edit Post' : 'New Post'}</span>
                  </h3>
                  <button
                    onClick={closeModal}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <FiX className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                      Title *
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      required
                      placeholder="Post title"
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-black dark:focus:border-white"
                    />
                  </div>

                  {/* Content (markdown) */}
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                      Content * <span className="font-normal text-gray-400">(markdown supported)</span>
                    </label>
                    <textarea
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      required
                      rows={8}
                      placeholder="Write your post in markdown..."
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-black dark:focus:border-white font-mono text-sm resize-y"
                    />
                  </div>

                  {/* Category + Status row */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                        Category
                      </label>
                      <input
                        type="text"
                        list="categories"
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        placeholder="General"
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-black dark:focus:border-white"
                      />
                      <datalist id="categories">
                        <option value="General" />
                        <option value="Announcement" />
                        <option value="Project" />
                        <option value="Link" />
                      </datalist>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                        Status
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-black dark:focus:border-white"
                      >
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                      </select>
                    </div>
                  </div>

                  {/* URL (optional) */}
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                      External URL <span className="font-normal text-gray-400">(optional)</span>
                    </label>
                    <input
                      type="url"
                      value={formData.url}
                      onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                      placeholder="https://..."
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-black dark:focus:border-white"
                    />
                  </div>

                  {/* Publish At */}
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                      Publish Date <span className="font-normal text-gray-400">(leave blank to publish now)</span>
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.publish_at}
                      onChange={(e) => setFormData({ ...formData, publish_at: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-black dark:focus:border-white"
                    />
                    {formData.status === 'published' && formData.publish_at && new Date(formData.publish_at) > new Date() && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        This post will appear on the public feed after {new Date(formData.publish_at).toLocaleString()}.
                      </p>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex space-x-3 justify-end pt-2">
                    <motion.button
                      type="button"
                      onClick={closeModal}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      type="submit"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="btn-modern"
                    >
                      {editingPost ? 'Save Changes' : 'Create Post'}
                    </motion.button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        {/* Project Create/Edit Modal */}
        <AnimatePresence>
          {showProjectModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto"
              onClick={closeProjectModal}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="card-modern p-8 w-full max-w-3xl my-8"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold flex items-center space-x-2">
                    <FiBriefcase />
                    <span>{editingProject ? 'Edit Project' : 'New Project'}</span>
                  </h3>
                  <button
                    onClick={closeProjectModal}
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  >
                    <FiX className="w-6 h-6" />
                  </button>
                </div>

                <form onSubmit={handleProjectSubmit} className="space-y-5">
                  {/* Type + Status + Order row */}
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">Type</label>
                      <select
                        value={projectForm.type}
                        onChange={(e) => setProjectForm({ ...projectForm, type: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-black dark:focus:border-white"
                      >
                        <option value="case-study">Case Study</option>
                        <option value="product">Product</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">Status</label>
                      <select
                        value={projectForm.status}
                        onChange={(e) => setProjectForm({ ...projectForm, status: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-black dark:focus:border-white"
                      >
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">Sort Order</label>
                      <input
                        type="number"
                        value={projectForm.sort_order}
                        onChange={(e) => setProjectForm({ ...projectForm, sort_order: e.target.value })}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-black dark:focus:border-white"
                      />
                    </div>
                  </div>

                  {/* Slug */}
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                      URL Slug <span className="font-normal text-gray-400">(e.g. sponge → /projects/sponge)</span>
                    </label>
                    <input
                      type="text"
                      value={projectForm.slug}
                      onChange={(e) => setProjectForm({ ...projectForm, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                      placeholder="e.g. sponge"
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-black dark:focus:border-white font-mono text-sm"
                    />
                  </div>

                  {/* Company + Role */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">Company *</label>
                      <input
                        type="text"
                        value={projectForm.company}
                        onChange={(e) => setProjectForm({ ...projectForm, company: e.target.value })}
                        required
                        placeholder="e.g. Wait Works LLC"
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-black dark:focus:border-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">Role</label>
                      <input
                        type="text"
                        value={projectForm.role}
                        onChange={(e) => setProjectForm({ ...projectForm, role: e.target.value })}
                        placeholder="e.g. Founder & Developer"
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-black dark:focus:border-white"
                      />
                    </div>
                  </div>

                  {/* Headline */}
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                      {projectForm.type === 'product' ? 'Product Name *' : 'Headline *'}
                    </label>
                    <input
                      type="text"
                      value={projectForm.headline}
                      onChange={(e) => setProjectForm({ ...projectForm, headline: e.target.value })}
                      required
                      placeholder={projectForm.type === 'product' ? 'e.g. Sponge' : 'e.g. Drove $500K+ MRR...'}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-black dark:focus:border-white"
                    />
                  </div>

                  {/* Duration + Tags */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                        {projectForm.type === 'product' ? 'Duration (optional)' : 'Duration'}
                      </label>
                      <input
                        type="text"
                        value={projectForm.duration}
                        onChange={(e) => setProjectForm({ ...projectForm, duration: e.target.value })}
                        placeholder="e.g. Jun – Aug 2025"
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-black dark:focus:border-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                        Tags <span className="font-normal text-gray-400">(comma-separated)</span>
                      </label>
                      <input
                        type="text"
                        value={projectForm.tags}
                        onChange={(e) => setProjectForm({ ...projectForm, tags: e.target.value })}
                        placeholder="Swift, macOS, AI/ML"
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-black dark:focus:border-white"
                      />
                    </div>
                  </div>

                  {/* Summary */}
                  <div>
                    <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">Summary</label>
                    <textarea
                      value={projectForm.summary}
                      onChange={(e) => setProjectForm({ ...projectForm, summary: e.target.value })}
                      rows={3}
                      placeholder="Brief description..."
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-black dark:focus:border-white resize-y"
                    />
                  </div>

                  {/* === Case Study Fields === */}
                  {projectForm.type === 'case-study' && (
                    <>
                      {/* Metrics */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Metrics</label>
                          <button type="button" onClick={addMetric} className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-black dark:hover:border-white hover:text-black dark:hover:text-white transition-colors">
                            + Add Metric
                          </button>
                        </div>
                        <div className="space-y-3">
                          {projectForm.metrics.map((metric, i) => (
                            <div key={i} className="grid grid-cols-5 gap-2 items-end">
                              <select
                                value={metric.icon}
                                onChange={(e) => updateMetric(i, 'icon', e.target.value)}
                                className="px-2 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-xs focus:outline-none focus:border-black dark:focus:border-white"
                              >
                                {ICON_OPTIONS.map(opt => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                              <input
                                type="text"
                                value={metric.value}
                                onChange={(e) => updateMetric(i, 'value', e.target.value)}
                                placeholder="$500K+"
                                className="px-2 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-black dark:focus:border-white"
                              />
                              <input
                                type="text"
                                value={metric.label}
                                onChange={(e) => updateMetric(i, 'label', e.target.value)}
                                placeholder="Label"
                                className="px-2 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-black dark:focus:border-white"
                              />
                              <input
                                type="text"
                                value={metric.delta}
                                onChange={(e) => updateMetric(i, 'delta', e.target.value)}
                                placeholder="Delta"
                                className="px-2 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-black dark:focus:border-white"
                              />
                              <button type="button" onClick={() => removeMetric(i)} className="p-2 text-gray-400 hover:text-black dark:hover:text-white transition-colors">
                                <FiTrash2 className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Sections */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Sections</label>
                          <button type="button" onClick={addSection} className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-black dark:hover:border-white hover:text-black dark:hover:text-white transition-colors">
                            + Add Section
                          </button>
                        </div>
                        <div className="space-y-4">
                          {projectForm.sections.map((section, i) => (
                            <div key={i} className="border border-gray-200 dark:border-gray-700 p-4">
                              <div className="flex items-center justify-between mb-2">
                                <input
                                  type="text"
                                  value={section.title}
                                  onChange={(e) => updateSection(i, 'title', e.target.value)}
                                  placeholder="Section title (e.g. Context, Problem, Outcomes)"
                                  className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-black dark:focus:border-white"
                                />
                                <button type="button" onClick={() => removeSection(i)} className="ml-2 p-1 text-gray-400 hover:text-black dark:hover:text-white transition-colors">
                                  <FiTrash2 className="w-4 h-4" />
                                </button>
                              </div>
                              <textarea
                                value={section.content}
                                onChange={(e) => updateSection(i, 'content', e.target.value)}
                                rows={4}
                                placeholder="Section content..."
                                className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:border-black dark:focus:border-white resize-y"
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {/* === Product Fields === */}
                  {projectForm.type === 'product' && (
                    <>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">Tagline</label>
                          <input
                            type="text"
                            value={projectForm.tagline}
                            onChange={(e) => setProjectForm({ ...projectForm, tagline: e.target.value })}
                            placeholder="e.g. Lecture Recording & AI Notes"
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-black dark:focus:border-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">Platform</label>
                          <input
                            type="text"
                            value={projectForm.platform}
                            onChange={(e) => setProjectForm({ ...projectForm, platform: e.target.value })}
                            placeholder="e.g. macOS"
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-black dark:focus:border-white"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">Version</label>
                          <input
                            type="text"
                            value={projectForm.version}
                            onChange={(e) => setProjectForm({ ...projectForm, version: e.target.value })}
                            placeholder="e.g. v2.5"
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-black dark:focus:border-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">Requirements</label>
                          <input
                            type="text"
                            value={projectForm.requirements}
                            onChange={(e) => setProjectForm({ ...projectForm, requirements: e.target.value })}
                            placeholder="e.g. macOS 26 (Tahoe)"
                            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-black dark:focus:border-white"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                          Features <span className="font-normal text-gray-400">(one per line)</span>
                        </label>
                        <textarea
                          value={projectForm.features}
                          onChange={(e) => setProjectForm({ ...projectForm, features: e.target.value })}
                          rows={5}
                          placeholder="Real-time transcription&#10;AI-generated notes&#10;PDF export"
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-black dark:focus:border-white font-mono text-sm resize-y"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                          Tech Stack <span className="font-normal text-gray-400">(comma-separated)</span>
                        </label>
                        <input
                          type="text"
                          value={projectForm.techStack}
                          onChange={(e) => setProjectForm({ ...projectForm, techStack: e.target.value })}
                          placeholder="Swift, Gemini API, Whisper"
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-black dark:focus:border-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                          GitHub / Download URL <span className="font-normal text-gray-400">(optional)</span>
                        </label>
                        <input
                          type="url"
                          value={projectForm.githubUrl}
                          onChange={(e) => setProjectForm({ ...projectForm, githubUrl: e.target.value })}
                          placeholder="https://github.com/..."
                          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:border-black dark:focus:border-white"
                        />
                      </div>
                    </>
                  )}

                  {/* Actions */}
                  <div className="flex space-x-3 justify-end pt-2">
                    <motion.button
                      type="button"
                      onClick={closeProjectModal}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="px-6 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                    >
                      Cancel
                    </motion.button>
                    <motion.button
                      type="submit"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className="btn-modern"
                    >
                      {editingProject ? 'Save Changes' : 'Create Project'}
                    </motion.button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default AdminPanel;
